import { jwtDecode } from 'jwt-decode';
import React, { useState, useRef, useEffect, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  SidebarContainer,
  SidebarParteMenor,
  SidebarParteMaior,
  Aba,
  AbaLabel,
  MenuIcon,
  ListaAbas,
  AreaUsuario,
  ContainerUser,
  CircleIcon,
  UserInfo,
  UserName,
  UserLotacao,
  ToggleButton,
} from './styles';
import 'material-icons/iconfont/material-icons.css';
import logoSisdafNegativo from '../../assets/logo_sisdaf_negativo_trp.png';
import {
  hasTpVinculo,
  isAdmin,
  useNomeCompleto,
} from '../../modules/contexts/userContexts';
import { versaoAtualSisaf } from '../../pages/daf/AppDAF/versaoSisaf';
import theme from '../../theme';
import { clearAllCache, formataUserName } from '../../utils/functions';
import StyledLink from '../Link';
import MenuPopup from '../MenuPopup';

interface MenuLink {
  to: string;
  label: string;
  icon: string;
}

interface MenuConfig {
  subtitulo: string;
  versao: string;
  baseUrl: string;
  links: Array<MenuLink>;
}

const detectTenant = (p: string): 'daf' | 'secsaude' | 'fornecedor' => {
  const seg = p.split('/')[1]?.toLowerCase() || '';

  // normaliza "secsaude"
  if (['secsaude', 'sec-saude', 'ses', 'saude', 'sec_saude'].includes(seg)) {
    return 'secsaude';
  }

  // normaliza "fornecedor"
  if (['fornecedor', 'fornecedores', 'forn'].includes(seg)) {
    return 'fornecedor';
  }

  // default
  return 'daf';
};

const Sidebar: React.FC = () => {
  const { pathname } = useLocation();
  const tenant = detectTenant(pathname);

  const location = useLocation();
  const navigate = useNavigate();
  const getInitialCollapseState = () => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('sidebarCollapsed') === 'true';
  };

  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    getInitialCollapseState,
  );
  const [isMenuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { nomeCompleto, siglaInstituicao, papel, isLoading } =
    useNomeCompleto();

  // Inicialização com localStorage
  const [userName, setUserName] = useState<string>(
    () => localStorage.getItem('nomeCompleto') || 'Carregando...',
  );
  const [userSiglaInstituicao, setUserSiglaInstituicao] = useState<string>(
    () => localStorage.getItem('siglaInstituicao') || '',
  );

  useEffect(() => {
    if (!isLoading) {
      const formattedName = formataUserName(nomeCompleto) || 'Usuário';

      setUserName(formattedName);
      setUserSiglaInstituicao(siglaInstituicao || '');

      localStorage.setItem('nomeCompleto', formattedName);
      localStorage.setItem('siglaInstituicao', siglaInstituicao || '');
    }
  }, [nomeCompleto, siglaInstituicao, isLoading]);

  const isUserAdmin = isAdmin(papel) || hasTpVinculo();

  // ativa se rota atual começa com /tenant/ + to (usamos includes para subrotas)
  const checkisActive = (fullPath: string): boolean => {
    return location.pathname.startsWith(fullPath);
  };

  const buildMatchPath = (path: string): string => {
    // Garante barra inicial e remove barras duplicadas para evitar falsos negativos
    const sanitized = (path.startsWith('/') ? path : `/${path}`).replace(
      /\/{2,}/g,
      '/',
    );
    if (sanitized === '/') {
      return '/';
    }

    // Remove barra final quando existir para facilitar o split
    const trimmed =
      sanitized.endsWith('/') && sanitized.length > 1
        ? sanitized.slice(0, -1)
        : sanitized;

    const segments = trimmed.split('/');

    // Se houver ao menos duas partes depois do tenant, removemos a última
    // para que abas de módulos apontem para o diretório base (ex.: /daf/produtosdaf/)
    if (segments.length <= 3) {
      return trimmed;
    }

    // Enquanto houver mais de 3 partes, sinifica que há subrotas a partir do módulo principal (ex: /daf/produtosdaf/produtos)
    // ex em segments > ['', 'daf', 'produtosdaf', 'produtos']
    // se no futuro for adicionado outro nível de subrotas, essa função atenderá
    while (segments.length > 3) {
      segments.pop();
    }

    const basePath = segments.join('/') || '/';

    return basePath.endsWith('/') ? basePath : `${basePath}/`;
  };

  const toggleMenu = () => setMenuOpen((p) => !p);
  const toggleCollapse = () => setIsCollapsed((prev) => !prev);

  const handleLogout = async () => {
    try {
      // #Limpa TUDO do localStorage
      const keysToRemove = [
        'token',
        'refresh_token',
        'access_token',
        'coSes',
        'coFornecedor',
        'siglaInstituicao',
        'nomeCompleto',
        'sidebarCollapsed',
        'user',
        'userInfo',
        'authToken',
      ];

      keysToRemove.forEach((key) => localStorage.removeItem(key));

      // #Limpa TUDO do sessionStorage
      //console.log('Limpando sessionStorage...');
      sessionStorage.clear();

      // #Limpa cache
      if (typeof clearAllCache === 'function') {
        // console.log('Limpando cache...');
        clearAllCache();
      }

      // #Limpa cookies
      document.cookie.split(';').forEach((c) => {
        document.cookie = c
          .replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
      });

      // #Redireciona para o backend fazer logout no Gov.br
      // #o Gov.br vai limpar a sessão SSO e redirecionar de volta para FRONTEND_URL
      // const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      //window.location.href = '/';
      // const logoutUrl = `${apiUrl}/api/auth/govbr/logout`;

      // console.log('Redirecionando para:', logoutUrl);

      // #forçar redirecionamento completo
      // window.location.href = logoutUrl;
    } catch (error) {
      console.error('Erro no logout:', error);

      // #Em caso de erro, limpa o que conseguir e vai para a home
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) {
        console.error('Erro ao limpar storage:', e);
      }

      // Força redirecionamento para a home
      window.location.href = '/';
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside as any);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as any);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    if (isCollapsed) {
      setMenuOpen(false);
    }
  }, [isCollapsed]);

  const menuConfigs: Record<'daf' | 'secsaude' | 'fornecedor', MenuConfig> = {
    daf: {
      subtitulo: 'DAF',
      versao: versaoAtualSisaf.interfaceDAF,
      baseUrl: '/daf/home/inicio',
      links: [
        { to: 'home/inicio', label: 'Home', icon: 'home' },
        {
          to: 'produtosdaf/produtos',
          label: 'Produtos DAF',
          icon: 'medication',
        },
        {
          to: 'ses/ses',
          label: 'Estabelec. de Saúde',
          icon: 'medical_information',
        },
        {
          to: 'fornecedores/fornecedores',
          label: 'Fornecedores',
          icon: 'warehouse',
        },
        { to: 'proaq/processos', label: 'Aquisições', icon: 'account_tree' },
        // {
        //   to: 'logistica/fornecedor',
        //   label: 'Logística',
        //   icon: 'local_shipping',
        // },
      ],
    },
    secsaude: {
      subtitulo: 'Sec. Saúde',
      versao: versaoAtualSisaf.interfaceSES,
      baseUrl: '/secsaude/medsus/gestao',
      links: [
        // { to: 'home/inicio', label: 'Home', icon: 'home' },
        // { to: 'menu/ses', label: 'SES', icon: 'medical_services' },
        {
          to: 'medsus/gestao',
          label: 'MedSUS',
          icon: 'medication',
        },
      ],
    },
    fornecedor: {
      subtitulo: 'Fornecedor',
      versao: versaoAtualSisaf.interfaceFornecedor || '1.0.0',
      baseUrl: '/fornecedor/home/inicio',
      links: [
        { to: 'home/inicio', label: 'Home', icon: 'home' },
        //  { to: 'lotes/ordproducao', label: 'Lotes', icon: 'account_tree' },
        { to: 'contratos/contratos', label: 'Contratos', icon: 'description' },
        { to: 'pautas/pautas', label: 'Pautas', icon: 'event_note' },
      ],
    },
  };

  const config = menuConfigs[tenant];

  const token = localStorage.getItem('token');
  let papelTipo: 'AD' | 'SA' | 'US' | null = null;

  if (token) {
    try {
      const decoded: any = jwtDecode(token);
      const vinculos = Array.isArray(decoded?.vinculo) ? decoded.vinculo : [];

      const papeisVinculo = vinculos
        .map((v: any) => String(v?.papel || '').toUpperCase())
        .filter(Boolean);

      const superAdmin = vinculos.some((v: any) => v?.superAdmin === true);

      if (superAdmin) {
        papelTipo = 'SA';
      } else if (papeisVinculo.includes('ADMIN')) {
        papelTipo = 'AD';
      } else if (papeisVinculo.includes('USUÁRIO')) {
        papelTipo = 'US';
      } else {
        papelTipo = null;
      }
    } catch (err) {
      console.warn('Erro ao decodificar token:', err);
    }
  }

  return (
    <SidebarContainer $collapsed={isCollapsed}>
      <SidebarParteMenor $collapsed={isCollapsed}>
        <ToggleButton
          type="button"
          onClick={toggleCollapse}
          aria-label={
            isCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'
          }
          $collapsed={isCollapsed}
        >
          <span className="material-icons">
            {isCollapsed ? 'chevron_right' : 'chevron_left'}
          </span>
        </ToggleButton>
        <img src={logoSisdafNegativo} alt="Logo do SisDAF" />
        {!isCollapsed && (
          <>
            <span className="sidebar__titulo">
              Sis<b>AF</b>
            </span>
            <span className="sidebar__subtitulo">
              <b>{config.subtitulo}</b>
            </span>
            <span className="sidebar__versao">{config.versao}</span>
          </>
        )}
      </SidebarParteMenor>

      <SidebarParteMaior $collapsed={isCollapsed}>
        <ListaAbas $collapsed={isCollapsed}>
          {config.links.map((link) => {
            const to = `/${tenant}/${link.to}`.replace(/\/{2,}/g, '/');
            const fullMatchPath = buildMatchPath(to);
            return (
              <StyledLink
                key={link.to}
                to={to}
                title={isCollapsed ? link.label : undefined}
              >
                <Aba
                  $isActive={checkisActive(fullMatchPath)}
                  $collapsed={isCollapsed}
                >
                  <MenuIcon $collapsed={isCollapsed} className="material-icons">
                    {link.icon}
                  </MenuIcon>
                  <AbaLabel $collapsed={isCollapsed}>{link.label}</AbaLabel>
                </Aba>
              </StyledLink>
            );
          })}
        </ListaAbas>
      </SidebarParteMaior>

      <AreaUsuario $collapsed={isCollapsed}>
        <ContainerUser $collapsed={isCollapsed} onClick={toggleMenu}>
          <CircleIcon $collapsed={isCollapsed}>
            <span className="material-icons" aria-hidden="true">
              person
            </span>
          </CircleIcon>
          <UserInfo $collapsed={isCollapsed}>
            <UserName>{userName}</UserName>
            {userSiglaInstituicao && (
              <UserLotacao
                $collapsed={isCollapsed}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <span>
                  {tenant === 'fornecedor' ? '' : userSiglaInstituicao}
                </span>

                {papelTipo === 'AD' && (
                  <span
                    title="Perfil Administrador"
                    style={{
                      backgroundColor: '#FFF',
                      color: theme.colors.azulgov,
                      padding: '4px 6px',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      lineHeight: '1',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    AD
                  </span>
                )}

                {papelTipo === 'SA' && (
                  <span
                    title="Super Admin"
                    style={{
                      backgroundColor: theme.colors.vermelho,
                      color: '#fff',
                      padding: '4px 6px',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      lineHeight: '1',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    SA
                  </span>
                )}
              </UserLotacao>
            )}
          </UserInfo>
        </ContainerUser>

        <div ref={menuRef}>
          <MenuPopup isOpen={isMenuOpen} collapsed={isCollapsed}>
            <StyledLink
              to={`/${tenant}/meusdados/usuario`}
              onClick={toggleMenu}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                fontSize: '1.2rem',
              }}
            >
              <span
                className="material-icons"
                style={{
                  color: '#007bff',
                  fontSize: '1.2rem',
                  margin: '10px',
                }}
                aria-hidden="true"
              >
                person
              </span>
              Meus Dados
            </StyledLink>

            {tenant === 'daf' && (
              <StyledLink
                to="/daf/maisopcoes/tickets"
                onClick={toggleMenu}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  fontSize: '1.2rem',
                }}
              >
                <span
                  className="material-icons"
                  style={{
                    color: '#007bff',
                    fontSize: '1.2rem',
                    margin: '10px',
                  }}
                  aria-hidden="true"
                >
                  dashboard
                </span>
                Gestão SisAF
              </StyledLink>
            )}

            {isUserAdmin && (
              <StyledLink
                to={`/${tenant}/adm/usuarios`}
                onClick={toggleMenu}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  fontSize: '1.2rem',
                }}
              >
                <span
                  className="material-icons"
                  style={{
                    color: '#007bff',
                    fontSize: '1.2rem',
                    margin: '10px',
                  }}
                  aria-hidden="true"
                >
                  settings
                </span>
                Administração
              </StyledLink>
            )}

            <StyledLink
              onClick={handleLogout}
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                fontSize: '1.2rem',
              }}
            >
              <span
                className="material-icons"
                style={{
                  color: '#007bff',
                  fontSize: '1.2rem',
                  margin: '10px',
                }}
                aria-hidden="true"
              >
                logout
              </span>
              Sair
            </StyledLink>
          </MenuPopup>
        </div>
      </AreaUsuario>
    </SidebarContainer>
  );
};

export default memo(Sidebar);
